import { useState, useEffect } from 'react';
import { purchaseOrdersService, PurchaseOrder, PurchaseOrderWithItems } from '../services/purchaseOrdersService';
import { useAuth } from '../context/AuthContext';

export const usePurchaseOrders = () => {
  const { businessId } = useAuth();
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null); // ID of order being deleted

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const { orders: data, error: fetchError } = await purchaseOrdersService.getPurchaseOrders(businessId || undefined);

      if (fetchError) {
        throw fetchError;
      }

      setOrders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch purchase orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessId) {
      fetchPurchaseOrders();
    }
  }, [businessId]);

  const refreshPurchaseOrders = () => {
    fetchPurchaseOrders();
  };

  const createPurchaseOrder = async (orderData: any) => {
    try {
      setCreating(true);
      setError(null);

      const { order, error: createError } = await purchaseOrdersService.createPurchaseOrder(orderData, businessId || undefined);

      if (createError) {
        throw createError;
      }

      if (order) {
        setOrders(prev => [order, ...prev]);
      }

      return { order, error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to create purchase order');
      setError(error.message);
      return { order: null, error };
    } finally {
      setCreating(false);
    }
  };

  const deletePurchaseOrder = async (id: string) => {
    try {
      setDeleting(id);
      setError(null);

      const { error: deleteError } = await purchaseOrdersService.deletePurchaseOrder(id, businessId || undefined);

      if (deleteError) {
        throw deleteError;
      }

      setOrders(prev => prev.filter(order => order.id !== id));
      return { error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete purchase order');
      setError(error.message);
      return { error };
    } finally {
      setDeleting(null);
    }
  };

  return {
    orders,
    loading,
    error,
    creating,
    deleting,
    refreshPurchaseOrders,
    createPurchaseOrder,
    deletePurchaseOrder,
  };
};

export const usePurchaseOrder = (id: string | null) => {
  const { businessId } = useAuth();
  const [order, setOrder] = useState<PurchaseOrderWithItems | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    const fetchOrder = async () => {
      try {
        setLoading(true);
        setError(null);
        const { order: data, error: fetchError } = await purchaseOrdersService.getPurchaseOrderById(id, businessId || undefined);

        if (fetchError) {
          throw fetchError;
        }

        setOrder(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch purchase order');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id, businessId]);

  return {
    order,
    loading,
    error,
  };
};